import { useState, useEffect, useContext, Fragment, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, Chip, Divider, Stepper, Step, StepLabel, CircularProgress,
  IconButton, TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Collapse, Paper, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestoreIcon from '@mui/icons-material/Restore';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NotesIcon from '@mui/icons-material/Notes';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthProvider';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import warrantyTicketServices from '../services/warrantyTicketServices';
import suppliersServices from '../services/suppliersServices';
import userServices from '../services/userServices';
import propertiesServices from '../services/propertiesServices';
import { stringifyAddress } from '../utils/formatter';
import WarrantyTicketNextActionBanner from './WarrantyTicketNextActionBanner';
import WarrantyTicketPhotoSection from './WarrantyTicketPhotoSection';
import ScaFormDialog from './ScaFormDialog';
import {
  PartTypeToggle, partDescPlaceholder, partDescSx, PartsHeaderLabel, PartCardBadge,
  InstalledEquipmentPicker, installedEquipmentYear,
} from './WarrantyTicketFormSections';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';

const priorityColor = { High: '#d32f2f', Medium: '#ed6c02', Low: '#2e7d32' };
const statusColor = { Active: 'primary', 'In Progress': 'warning', Resolved: 'success' };
const STATUS_STEPS = ['Active', 'In Progress', 'Resolved'];

const emptyPart = () => ({
  partDescription: '',
  orderedFrom: '',
  orderDate: '',
  expectedArrival: '',
  poNumber: '',
  qty: 1,
  instruction: '',
  supplierId: null,
  itemType: 'part',
  failedPartNumber: '',
  failedPartSerial: 'N/A',
});

export default function WarrantyTicketDialog({ open, onClose, ticketId, onUpdate }) {
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editParts, setEditParts] = useState([]);
  const [propertyEquipment, setPropertyEquipment] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  const [resolveOpen, setResolveOpen] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTechId, setReassignTechId] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [reassigning, setReassigning] = useState(false);
  const [scaOpen, setScaOpen] = useState(false);

  const isSuperAdmin = userData?.role === 'superadmin';
  const isAdmin = isSuperAdmin || userData?.role === 'admin';
  const isTechnician = userData?.role === 'technician';
  const isDispatcher = userData?.role === 'dispatcher';
  const canEdit = isAdmin || (isTechnician && ticket?.assignedTech?.id === userData?.id);
  const canDelete = isAdmin;
  const isReadOnly = isDispatcher;

  useEffect(() => {
    if (open && ticketId) {
      setLoading(true);
      setTicket(null);
      setEditing(false);
      warrantyTicketServices.getById(ticketId).then(res => {
        if (res.status === 200) setTicket(res.data);
      }).catch(e => {
        toast.error('Error loading ticket');
        console.error(e);
      }).finally(() => setLoading(false));
    }
  }, [open, ticketId]);

  useEffect(() => {
    if (open) {
      suppliersServices.getSuppliers().then(res => {
        if (res.status === 200) setSuppliers(res.data.value?.data || res.data?.data || []);
      }).catch(() => {});
    }
  }, [open]);

  // Installed Equipment of the ticket's property, for the defective-equipment
  // picker in edit mode (the ticket payload doesn't include it).
  useEffect(() => {
    const propId = ticket?.property?.id;
    if (!open || !propId) { setPropertyEquipment([]); return; }
    propertiesServices.getPropertyById(propId).then(res => {
      if (res.status === 200) setPropertyEquipment(res.data?.equipment || []);
    }).catch(() => setPropertyEquipment([]));
  }, [open, ticket?.property?.id]);

  const startEditing = () => {
    setEditForm({
      installationYear: ticket.installationYear || null,
      brandModel: ticket.brandModel || '',
      defectiveModel: ticket.defectiveModel || '',
      defectiveSerial: ticket.defectiveSerial || '',
      issueDescription: ticket.issueDescription || '',
      priority: ticket.priority || 'Medium',
      workStatus: ticket.workStatus || '',
      partsLocation: ticket.partsLocation || '',
      followUpDate: ticket.followUpDate ? ticket.followUpDate.slice(0, 10) : '',
      followUpTime: ticket.followUpTime || '',
      followUpReason: ticket.followUpReason || '',
    });
    setEditParts(
      (ticket.parts && ticket.parts.length > 0)
        ? ticket.parts.map(p => {
            let supplierId = null;
            if (p.orderedFrom === 'Ordered by Phone') {
              supplierId = 'phone';
            } else if (p.orderedFrom) {
              const matched = suppliers.find(s => s.name === p.orderedFrom);
              if (matched) supplierId = matched.id;
            }
            return {
              id: p.id,
              partDescription: p.partDescription || '',
              orderedFrom: p.orderedFrom || '',
              orderDate: p.orderDate ? p.orderDate.slice(0, 10) : '',
              expectedArrival: p.expectedArrival ? p.expectedArrival.slice(0, 10) : '',
              poNumber: p.poNumber || '',
              qty: p.qty || 1,
              instruction: p.instruction || '',
              supplierId,
              itemType: (p.failedPartNumber && p.failedPartNumber === (ticket.defectiveModel || '')) ? 'equipment' : 'part',
              failedPartNumber: p.failedPartNumber || '',
              failedPartSerial: p.failedPartSerial || '',
            };
          })
        : []
    );
    setExpandedRows({});
    setEditing(true);
  };

  const cancelEditing = () => { setEditing(false); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        installationYear: editForm.installationYear || null,
        brandModel: editForm.brandModel,
        defectiveModel: editForm.defectiveModel,
        defectiveSerial: editForm.defectiveSerial,
        issueDescription: editForm.issueDescription,
        priority: editForm.priority,
        workStatus: editForm.workStatus,
        partsLocation: editForm.partsLocation,
        followUpDate: editForm.followUpDate || undefined,
        followUpTime: editForm.followUpTime || undefined,
        followUpReason: editForm.followUpReason || undefined,
        parts: editParts.filter(p => p.partDescription.trim()).map(p => ({
          partDescription: p.partDescription,
          orderedFrom: p.orderedFrom || undefined,
          orderDate: p.orderDate || undefined,
          expectedArrival: p.expectedArrival || undefined,
          poNumber: p.poNumber || undefined,
          qty: p.qty || 1,
          instruction: p.instruction || undefined,
          failedPartNumber: p.failedPartNumber || undefined,
          failedPartSerial: p.failedPartSerial || undefined,
        })),
      };
      const res = await warrantyTicketServices.update(ticketId, data);
      if (res.status === 200) {
        const savedTicket = res.data;
        setTicket(savedTicket);
        setEditing(false);
        toast.success('Ticket updated');
        if (onUpdate) onUpdate();

        const originalPartIds = new Set((ticket.parts || []).map(p => p.id));
        const originalSupplierMap = {};
        (ticket.parts || []).forEach(p => { if (p.id) originalSupplierMap[p.id] = p.orderedFrom || ''; });

        const newOrChangedParts = editParts.filter(p => {
          if (!p.supplierId || !p.partDescription.trim()) return false;
          if (!p.id || !originalPartIds.has(p.id)) return true;
          const origSupplier = originalSupplierMap[p.id] || '';
          return p.orderedFrom !== origSupplier;
        });

        if (newOrChangedParts.length > 0) {
          const newSupplierIds = new Set(newOrChangedParts.map(p => p.supplierId));
          const relevantSuppliers = selectedSuppliers.filter(s => newSupplierIds.has(s.id));

          if (relevantSuppliers.length > 0) {
            try {
              const address = savedTicket.property ? stringifyAddress(savedTicket.property) : '';
              const techName = userData?.fullName || '';
              const techEmail = userData?.email || '';
              const techPhone = userData?.phoneNumber || '';
              let contactLine = '';
              if (techEmail && techPhone) contactLine = `\nYou can contact me by email: ${techEmail} and cell phone: ${techPhone}`;
              else if (techEmail) contactLine = `\nYou can contact me by email: ${techEmail}`;
              else if (techPhone) contactLine = `\nYou can contact me by cell phone: ${techPhone}`;

              const orders = relevantSuppliers.map(s => {
                let emails = [];
                if (s.id === 'other' || s.id === 'phone') { emails = []; }
                else if (s.emails && Array.isArray(s.emails)) { emails = s.emails; }
                else if (s.email) { emails = [s.email]; }

                const sParts = newOrChangedParts.filter(p => p.supplierId === s.id);
                const partsList = sParts.map(p => `${p.partDescription}${p.qty > 1 ? ` (x${p.qty})` : ''}`).join('\n');
                const instructions = sParts.filter(p => p.instruction).map(p => `Note: ${p.instruction}`).join('\n');
                const supplierName = s.id === 'other' ? 'Supplier' : (s.name || 'Supplier');

                const defModel = savedTicket.defectiveModel ? `Model: ${savedTicket.defectiveModel}` : '';
                const defSerial = savedTicket.defectiveSerial ? `Serial #: ${savedTicket.defectiveSerial}` : '';
                const defectiveLine = [defModel, defSerial].filter(Boolean).join('\n');

                const emailContent = `*** PLEASE DO NOT REPLY TO THIS EMAIL — this mailbox is not monitored. ***\n*** For any questions regarding this order, please use the contact information below. ***\n\nDear ${supplierName}\n\nPlease process this equipment order:\n\n${address ? `Property: ${address}\n` : ''}PO: ${savedTicket.ticketNumber}\n${defectiveLine ? `\n${defectiveLine}\n` : ''}\nParts needed:\n${partsList}\n${instructions ? `\n${instructions}\n` : ''}${contactLine}\n\nThank you${techName ? `\n${techName}` : ''}`;

                return { supplierName: s.name, emails, emailContent, items: sParts.map(p => ({ partDescription: p.partDescription, qty: p.qty || 1, instruction: p.instruction || undefined })) };
              }).filter(o => o.emails.length > 0);

              if (orders.length > 0) {
                const res = await warrantyTicketServices.sendPartsOrder(ticketId, orders, userData?.email);
                toast.success('Order email sent to suppliers!');
                const wh = res?.data?.warehouse;
                if (wh?.warning) {
                  toast.warning(`Warehouse: ${wh.warning}`);
                } else if (wh?.created > 0) {
                  toast.info(`Warehouse: ${wh.created} item(s) reserved${wh.skipped ? `, ${wh.skipped} already existed` : ''}`);
                }
              }
            } catch (emailErr) {
              console.error('Failed to send order email:', emailErr);
              toast.warning('Saved but email sending failed');
            }
          }
        }
      }
    } catch (e) {
      toast.error('Error saving changes');
      console.error(e);
    }
    setSaving(false);
  };

  const handleEditFormChange = (field, value) => {
    // Keep equipment-mode failed-part serials in sync with the defective serial.
    if (field === 'defectiveSerial') {
      const oldSerial = (editForm.defectiveSerial || '').trim();
      const next = (value || '').toUpperCase();
      setEditParts(prev => prev.map(p => {
        if ((p.itemType || 'part') !== 'equipment') return p;
        const cur = (p.failedPartSerial || '').trim();
        if (cur === '' || cur === 'N/A' || cur === oldSerial) return { ...p, failedPartSerial: next || 'N/A' };
        return p;
      }));
      value = next;
    }
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  // Mirror the create form: the defective model is upper-cased and auto-copied
  // into the first part's description, unless the tech already typed something
  // else there.
  const handleEditDefectiveModelChange = (rawValue) => {
    const value = (rawValue || '').toUpperCase();
    const oldModel = (editForm.defectiveModel || '').trim();
    setEditParts(prev => prev.map((p, i) => {
      let next = p;
      const failed = (p.failedPartNumber || '').trim();
      const failedFollows = failed === '' || failed === oldModel;
      if (i === 0) {
        const desc = (p.partDescription || '').trim();
        if (desc === '' || desc === oldModel) {
          next = { ...next, partDescription: value };
          if ((p.itemType || 'part') === 'part' && failedFollows) next = { ...next, failedPartNumber: value };
        }
      }
      if ((p.itemType || 'part') === 'equipment' && failedFollows) {
        next = { ...next, failedPartNumber: value };
      }
      return next;
    }));
    setEditForm(prev => ({ ...prev, defectiveModel: value }));
  };

  // Force upper-case for identifier-like part fields (model/part numbers, PO,
  // manual supplier name). Special Instructions is free prose, so it is excluded.
  const UPPERCASE_PART_FIELDS = ['partDescription', 'poNumber', 'orderedFrom', 'failedPartNumber', 'failedPartSerial'];

  const handlePartChange = (index, field, value) => {
    const v = (UPPERCASE_PART_FIELDS.includes(field) && typeof value === 'string')
      ? value.toUpperCase()
      : value;
    setEditParts(prev => prev.map((p, i) => {
      if (i !== index) return p;
      // In "part" mode the failed part # follows the description while the
      // tech hasn't typed a custom value into it.
      if (field === 'partDescription' && (p.itemType || 'part') === 'part') {
        const failed = (p.failedPartNumber || '').trim();
        const oldDesc = (p.partDescription || '').trim();
        if (failed === '' || failed === oldDesc) {
          return { ...p, partDescription: v, failedPartNumber: v };
        }
      }
      return { ...p, [field]: v };
    }));
  };

  // Part / Equipment toggle: prefill the failed-part fields accordingly.
  const handlePartItemTypeChange = (index, itemType) => {
    setEditParts(prev => prev.map((p, i) => {
      if (i !== index) return p;
      if (itemType === 'equipment') {
        return {
          ...p,
          itemType,
          failedPartNumber: (editForm.defectiveModel || '').toUpperCase(),
          failedPartSerial: (editForm.defectiveSerial || '').toUpperCase() || 'N/A',
        };
      }
      return {
        ...p,
        itemType,
        failedPartNumber: p.partDescription || '',
        failedPartSerial: 'N/A',
      };
    }));
  };

  const handlePartSupplierChange = (index, supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setEditParts(prev => prev.map((p, i) => i === index ? {
      ...p,
      supplierId,
      orderedFrom: supplierId === 'phone' ? 'Ordered by Phone' : supplierId === 'other' ? '' : (supplier?.name || '')
    } : p));
  };

  const toggleRowExpanded = (index) => {
    setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const addPart = () => setEditParts(prev => [...prev, emptyPart()]);

  const removePart = (index) => {
    setEditParts(prev => prev.filter((_, i) => i !== index));
    setExpandedRows(prev => {
      const next = {};
      Object.keys(prev).forEach(k => {
        const ki = parseInt(k);
        if (ki < index) next[ki] = prev[ki];
        else if (ki > index) next[ki - 1] = prev[ki];
      });
      return next;
    });
  };

  // eslint-disable-next-line no-unused-vars
  const selectedSuppliers = useMemo(() => {
    const ids = new Set();
    const result = [];
    editParts.forEach(p => {
      if (p.supplierId && !ids.has(p.supplierId)) {
        ids.add(p.supplierId);
        if (p.supplierId === 'phone') {
          result.push({ id: 'phone', name: 'Ordered by Phone', emails: [] });
        } else if (p.supplierId === 'other') {
          result.push({ id: 'other', name: p.orderedFrom || 'Other', emails: [] });
        } else {
          const s = suppliers.find(sup => sup.id === p.supplierId);
          if (s) result.push(s);
        }
      }
    });
    return result;
  }, [editParts, suppliers]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const openReassign = async () => {
    if (technicians.length === 0) {
      try {
        const res = await userServices.getAllUsers(0, 100, false);
        if (res.status === 200 || res.status === 206) {
          const techs = (res.data?.data || []).filter(u => u.role === 'technician' || u.role === 'admin' || u.role === 'superadmin');
          setTechnicians(techs);
        }
      } catch (e) { /* ignore */ }
    }
    setReassignTechId(ticket?.assignedTech?.id || '');
    setReassignOpen(true);
  };

  const handleReassign = async () => {
    if (!reassignTechId) return;
    setReassigning(true);
    try {
      const res = await warrantyTicketServices.update(ticketId, { assignedTechId: reassignTechId });
      if (res.status === 200) {
        setTicket(res.data);
        toast.success('Technician reassigned');
        setReassignOpen(false);
        if (onUpdate) onUpdate();
      }
    } catch (e) {
      toast.error('Error reassigning ticket');
    }
    setReassigning(false);
  };

  const handleStatusChange = async () => {
    try {
      const res = await warrantyTicketServices.changeStatus(ticketId, newStatus);
      if (res.status === 200) {
        setTicket(res.data);
        toast.success(`Status changed to ${newStatus}`);
        if (onUpdate) onUpdate();
      }
    } catch (e) { toast.error('Error changing status'); }
    setStatusDialogOpen(false);
  };

  const handleResolve = async () => {
    try {
      const res = await warrantyTicketServices.resolve(ticketId, completionNotes);
      if (res.status === 200) {
        setTicket(res.data);
        toast.success('Ticket resolved');
        if (onUpdate) onUpdate();
      }
    } catch (e) { toast.error('Error resolving ticket'); }
    setResolveOpen(false);
    setCompletionNotes('');
  };

  const handleDelete = async () => {
    try {
      await warrantyTicketServices.remove(ticketId);
      toast.success('Ticket deleted');
      if (onUpdate) onUpdate();
      onClose();
    } catch (e) { toast.error('Error deleting ticket'); }
    setDeleteConfirm(false);
  };

  const stepIdx = ticket ? STATUS_STEPS.indexOf(ticket.status) : 0;
  const activeStep = stepIdx >= 0 ? stepIdx : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {ticket?.ticketNumber || 'Warranty Ticket'}
          </Typography>
          {ticket && (
            <>
              <Chip label={ticket.status} color={statusColor[ticket.status] || 'default'} size="small" />
              <Chip label={ticket.priority} size="small" sx={{ backgroundColor: priorityColor[ticket.priority], color: '#fff', fontWeight: 600 }} />
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => navigate(`/warranty-tickets/${ticketId}`)} title="Open full page">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : !ticket ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Ticket not found</Typography>
        ) : (
          <>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
              {STATUS_STEPS.map((label) => (
                <Step key={label} completed={STATUS_STEPS.indexOf(label) <= activeStep}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <WarrantyTicketNextActionBanner ticket={ticket} sx={{ mb: 2 }} />

            {/* Customer & Address */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Address</Typography>
                <Typography variant="body2">{ticket.property ? stringifyAddress(ticket.property) : '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Customer</Typography>
                <Typography variant="body2">
                  {ticket.client ? `${ticket.client.firstName || ''} ${ticket.client.lastName || ''}`.trim() : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body2">{ticket.customerPhone || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Proposal</Typography>
                <Typography variant="body2">
                  {ticket.notAirmaxxInstallation
                    ? 'Not Airmaxx Installation'
                    : (
                      <>
                        {ticket.proposal?.publicId || ticket.legacyProposalNumber || '—'}
                        {ticket.legacyProposalNumber && ' (Legacy)'}
                      </>
                    )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Assigned Tech</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2">
                    {ticket.assignedTech
                      ? `${ticket.assignedTech.firstName || ''} ${ticket.assignedTech.lastName || ''}`.trim()
                      : '—'}
                  </Typography>
                  {isSuperAdmin && !editing && (
                    <IconButton size="small" onClick={openReassign} title="Reassign technician"
                      sx={{ p: 0.25, color: 'primary.main' }}>
                      <SwapHorizIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body2">{formatDateTime(ticket.createdAt)}</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* ===== READ MODE ===== */}
            {!editing ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>System & Issue</Typography>
                  {canEdit && !isReadOnly && (
                    <Button size="small" startIcon={<EditIcon />} onClick={startEditing}>Edit</Button>
                  )}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Installation Year</Typography>
                    <Typography variant="body2">{ticket.installationYear || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Brand</Typography>
                    <Typography variant="body2">{ticket.brandModel || '—'}</Typography>
                  </Box>
                  {(ticket.defectiveModel || ticket.defectiveSerial) && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Defective Equipment</Typography>
                      <Typography variant="body2">
                        {ticket.defectiveModel || '—'}
                        {ticket.defectiveSerial && ` • S/N: ${ticket.defectiveSerial}`}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">Work Status</Typography>
                    <Typography variant="body2">{ticket.workStatus || '—'}</Typography>
                  </Box>
                  {ticket.partsLocation && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Parts Location</Typography>
                      <Typography variant="body2">{ticket.partsLocation}</Typography>
                    </Box>
                  )}
                  {ticket.resolvedAt && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Resolved</Typography>
                      <Typography variant="body2">{formatDateTime(ticket.resolvedAt)}</Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Issue Description</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{ticket.issueDescription}</Typography>
                </Box>

                {ticket.completionNotes && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Completion Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{ticket.completionNotes}</Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1.5 }} />
                <WarrantyTicketPhotoSection ticket={ticket} canEdit={canEdit} />

                {ticket.parts && ticket.parts.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Parts ({ticket.parts.length})</Typography>
                    {ticket.parts.map((part) => (
                      <Box key={part.id} sx={{ p: 1.5, mb: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{part.partDescription}</Typography>
                          {part.qty > 1 && <Chip label={`x${part.qty}`} size="small" variant="outlined" />}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                          {part.orderedFrom && <Typography variant="caption" color="text.secondary">
                            {part.orderedFrom === 'Ordered by Phone' ? 'Ordered by Phone' : `From: ${part.orderedFrom}`}
                          </Typography>}
                          {part.poNumber && <Typography variant="caption" color="text.secondary">PO: {part.poNumber}</Typography>}
                          {part.orderDate && <Typography variant="caption" color="text.secondary">Ordered: {formatDate(part.orderDate)}</Typography>}
                          {part.expectedArrival && <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>ETA: {formatDate(part.expectedArrival)}</Typography>}
                        </Box>
                        {(part.failedPartNumber || part.failedPartSerial) && (
                          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
                            Failed part: {part.failedPartNumber || '—'}{part.failedPartSerial ? ` (SN: ${part.failedPartSerial})` : ''}
                          </Typography>
                        )}
                        {part.instruction && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            Note: {part.instruction}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </>
                )}
              </>
            ) : (
              /* ===== EDIT MODE ===== */
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Edit — System & Issue</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" startIcon={<CancelIcon />} onClick={cancelEditing} disabled={saving}>Cancel</Button>
                    <Button size="small" variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} onClick={handleSave} disabled={saving}>Save</Button>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="Installation Year"
                      views={['year']}
                      inputFormat="YYYY"
                      value={editForm.installationYear ? dayjs(`${editForm.installationYear}-01-01`) : null}
                      maxDate={dayjs()}
                      onChange={(v) => handleEditFormChange('installationYear', v && v.isValid() ? v.year() : null)}
                      renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    />
                  </LocalizationProvider>
                  <TextField label="Brand" value={editForm.brandModel} onChange={(e) => handleEditFormChange('brandModel', e.target.value.toUpperCase())} inputProps={{ style: { textTransform: 'uppercase' } }} size="small" fullWidth />
                </Box>

                <InstalledEquipmentPicker
                  equipment={propertyEquipment}
                  size="small"
                  sx={{ mb: 2 }}
                  onPick={(item) => {
                    handleEditDefectiveModelChange((item.model || '').toUpperCase());
                    const year = installedEquipmentYear(item);
                    setEditForm(prev => ({
                      ...prev,
                      defectiveSerial: (item.serialNumber || '').toUpperCase(),
                      ...(year ? { installationYear: year } : {}),
                    }));
                  }}
                />

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField label="Defective Equipment Model" value={editForm.defectiveModel}
                    onChange={(e) => handleEditDefectiveModelChange(e.target.value)}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    placeholder="e.g. CARRIER 24ACC636A003" size="small" fullWidth />
                  <TextField label="Defective Equipment Serial #" value={editForm.defectiveSerial}
                    onChange={(e) => handleEditFormChange('defectiveSerial', e.target.value.toUpperCase())}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    placeholder="e.g. 1234567890" size="small" fullWidth />
                </Box>

                <TextField
                  label="Issue Description" value={editForm.issueDescription}
                  onChange={(e) => handleEditFormChange('issueDescription', e.target.value)}
                  size="small" fullWidth multiline rows={3} sx={{ mb: 2 }}
                />

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Priority</InputLabel>
                  <Select value={editForm.priority || 'Medium'} label="Priority" onChange={(e) => handleEditFormChange('priority', e.target.value)}>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Low">Low</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Work Status</InputLabel>
                  <Select value={editForm.workStatus} label="Work Status" onChange={(e) => handleEditFormChange('workStatus', e.target.value)}>
                    <MenuItem value="Completed on-site">Completed on-site</MenuItem>
                    <MenuItem value="Parts needed">Parts needed</MenuItem>
                    <MenuItem value="Parts ordered">Parts ordered</MenuItem>
                    <MenuItem value="Parts received">Parts received</MenuItem>
                    <MenuItem value="Follow-up scheduled">Follow-up scheduled</MenuItem>
                    <MenuItem value="Follow-up needed">Follow-up needed</MenuItem>
                  </Select>
                </FormControl>

                {editForm.workStatus === 'Parts received' && (
                  <TextField
                    label="Parts Location" value={editForm.partsLocation}
                    onChange={(e) => handleEditFormChange('partsLocation', e.target.value.toUpperCase())}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    size="small" fullWidth placeholder="e.g. WAREHOUSE A, SHELF 3" sx={{ mb: 2 }}
                  />
                )}

                {(editForm.workStatus === 'Follow-up scheduled' || editForm.workStatus === 'Follow-up needed' || editForm.followUpDate) && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                    <TextField
                      label="Follow-up Date" type="date" size="small" fullWidth
                      value={editForm.followUpDate || ''}
                      onChange={(e) => handleEditFormChange('followUpDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Follow-up Time" size="small" fullWidth
                      value={editForm.followUpTime || ''}
                      onChange={(e) => handleEditFormChange('followUpTime', e.target.value)}
                      placeholder="e.g. 10:00 AM"
                    />
                    <TextField
                      label="Follow-up Reason" size="small" fullWidth
                      value={editForm.followUpReason || ''}
                      onChange={(e) => handleEditFormChange('followUpReason', e.target.value)}
                      sx={{ gridColumn: '1 / -1' }}
                    />
                  </Box>
                )}

                {/* Parts — table form like create page */}
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingCartIcon fontSize="small" />
                  Parts ({editParts.length})
                </Typography>

                {isMobile ? (
                  /* Mobile: vertical cards */
                  <Box sx={{ mb: 1.5 }}>
                    {editParts.map((part, index) => (
                      <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <PartCardBadge part={part} index={index} />
                          <IconButton size="small" color="error" onClick={() => removePart(index)}><RemoveCircleOutlineIcon fontSize="small" /></IconButton>
                        </Box>
                        <PartTypeToggle value={part.itemType} onChange={(v) => handlePartItemTypeChange(index, v)} sx={{ mb: 1 }} />
                        <TextField placeholder={partDescPlaceholder(part)} value={part.partDescription}
                          onChange={(e) => handlePartChange(index, 'partDescription', e.target.value)}
                          size="small" fullWidth sx={{ mb: 1, ...partDescSx(part) }} />
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField label="Qty" type="number" size="small" value={part.qty}
                            onChange={(e) => handlePartChange(index, 'qty', Math.max(1, parseInt(e.target.value) || 1))}
                            inputProps={{ min: 1 }} sx={{ width: 80 }} />
                          <FormControl fullWidth size="small">
                            <InputLabel>Supplier</InputLabel>
                            <Select value={part.supplierId || ''} label="Supplier" onChange={(e) => handlePartSupplierChange(index, e.target.value)}>
                              <MenuItem value=""><em>— Select —</em></MenuItem>
                              {suppliers.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                              <MenuItem value="phone"><em>Ordered by Phone</em></MenuItem>
                              <MenuItem value="other"><em>Other</em></MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        {part.supplierId === 'other' && (
                          <TextField placeholder="Supplier name" value={part.orderedFrom}
                            onChange={(e) => handlePartChange(index, 'orderedFrom', e.target.value)}
                            size="small" fullWidth sx={{ mb: 1 }} />
                        )}
                        {!part.supplierId && part.orderedFrom && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{part.orderedFrom}</Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField label="PO" placeholder="Auto" value={part.poNumber}
                            InputProps={{ readOnly: true }} helperText="Auto-filled from ticket number"
                            size="small" sx={{ flex: 1 }} />
                          <TextField label="ETA" type="date" value={part.expectedArrival}
                            onChange={(e) => handlePartChange(index, 'expectedArrival', e.target.value)}
                            size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                        </Box>
                        <TextField label="Special Instructions" placeholder="e.g., Need exact OEM part only..."
                          fullWidth size="small" value={part.instruction || ''}
                          onChange={(e) => handlePartChange(index, 'instruction', e.target.value)} sx={{ mb: 1 }} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField label="Failed Part #" placeholder="e.g. HC39GE237" value={part.failedPartNumber || ''}
                            onChange={(e) => handlePartChange(index, 'failedPartNumber', e.target.value)}
                            size="small" sx={{ flex: 1 }} />
                          <TextField label="Failed Part Serial" placeholder="Serial #" value={part.failedPartSerial || ''}
                            onChange={(e) => handlePartChange(index, 'failedPartSerial', e.target.value)}
                            size="small" sx={{ flex: 1 }} />
                        </Box>
                      </Paper>
                    ))}
                    {editParts.length === 0 && (
                      <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No parts — click "Add Part" to add</Typography>
                    )}
                  </Box>
                ) : (
                  /* Desktop: table */
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell width="5%" align="center" />
                          <TableCell width="28%"><PartsHeaderLabel rows={editParts} /></TableCell>
                          <TableCell width="8%" align="center"><strong>Qty</strong></TableCell>
                          <TableCell width="22%"><strong>Supplier</strong></TableCell>
                          <TableCell width="13%"><strong>PO</strong></TableCell>
                          <TableCell width="13%"><strong>ETA</strong></TableCell>
                          <TableCell width="5%" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {editParts.map((part, index) => (
                          <Fragment key={index}>
                            <TableRow hover>
                              <TableCell align="center" sx={{ p: 0.5 }}>
                                <IconButton size="small" onClick={() => toggleRowExpanded(index)}
                                  sx={{ color: (part.instruction || part.failedPartNumber || part.failedPartSerial) ? 'primary.main' : 'grey.400' }}>
                                  {expandedRows[index]
                                    ? <KeyboardArrowUpIcon fontSize="small" />
                                    : (part.instruction || part.failedPartNumber || part.failedPartSerial)
                                      ? <NotesIcon fontSize="small" />
                                      : <KeyboardArrowDownIcon fontSize="small" />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PartTypeToggle value={part.itemType} onChange={(v) => handlePartItemTypeChange(index, v)} />
                                  <TextField placeholder={partDescPlaceholder(part)} value={part.partDescription}
                                    onChange={(e) => handlePartChange(index, 'partDescription', e.target.value)}
                                    size="small" fullWidth variant="standard" sx={partDescSx(part)} />
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <TextField type="number" size="small" value={part.qty}
                                  onChange={(e) => handlePartChange(index, 'qty', Math.max(1, parseInt(e.target.value) || 1))}
                                  inputProps={{ min: 1, style: { textAlign: 'center', width: 45 } }} variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <FormControl fullWidth size="small">
                                  <Select value={part.supplierId || ''} onChange={(e) => handlePartSupplierChange(index, e.target.value)} displayEmpty>
                                    <MenuItem value=""><em>— Select —</em></MenuItem>
                                    {suppliers.map((s) => (<MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>))}
                                    <MenuItem value="phone"><em>Ordered by Phone</em></MenuItem>
                                    <MenuItem value="other"><em>Other (manual)</em></MenuItem>
                                  </Select>
                                </FormControl>
                                {part.supplierId === 'other' && (
                                  <TextField placeholder="Supplier name" value={part.orderedFrom}
                                    onChange={(e) => handlePartChange(index, 'orderedFrom', e.target.value)}
                                    size="small" fullWidth variant="standard" sx={{ mt: 0.5 }} />
                                )}
                                {!part.supplierId && part.orderedFrom && (
                                  <Typography variant="caption" color="text.secondary">{part.orderedFrom}</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <TextField placeholder="Auto" value={part.poNumber}
                                  InputProps={{ readOnly: true }}
                                  size="small" fullWidth variant="standard" />
                              </TableCell>
                              <TableCell>
                                <TextField type="date" value={part.expectedArrival}
                                  onChange={(e) => handlePartChange(index, 'expectedArrival', e.target.value)}
                                  size="small" fullWidth InputLabelProps={{ shrink: true }} variant="standard" />
                              </TableCell>
                              <TableCell align="center" sx={{ p: 0.5 }}>
                                <IconButton size="small" color="error" onClick={() => removePart(index)}>
                                  <RemoveCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={7} sx={{ p: 0, borderBottom: expandedRows[index] ? undefined : 'none' }}>
                                <Collapse in={expandedRows[index]} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                      <TextField label="Special Instructions" placeholder="e.g., Need exact OEM part only..."
                                        fullWidth size="small" multiline rows={2} value={part.instruction || ''}
                                        onChange={(e) => handlePartChange(index, 'instruction', e.target.value)}
                                        InputProps={{ startAdornment: <NoteAddIcon sx={{ mr: 1, color: 'grey.500' }} /> }} />
                                      <TextField label="Order Date" type="date" value={part.orderDate}
                                        onChange={(e) => handlePartChange(index, 'orderDate', e.target.value)}
                                        InputLabelProps={{ shrink: true }} size="small" sx={{ minWidth: 160 }} />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                      <TextField label="Failed Part #" placeholder="Part # of the defective component, e.g. HC39GE237"
                                        value={part.failedPartNumber || ''} onChange={(e) => handlePartChange(index, 'failedPartNumber', e.target.value)}
                                        size="small" fullWidth />
                                      <TextField label="Failed Part Serial" placeholder="Serial # (if available)"
                                        value={part.failedPartSerial || ''} onChange={(e) => handlePartChange(index, 'failedPartSerial', e.target.value)}
                                        size="small" fullWidth />
                                    </Box>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </Fragment>
                        ))}
                        {editParts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <Typography color="text.secondary" sx={{ py: 2 }}>No parts — click "Add Part" to add</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Button size="small" startIcon={<AddIcon />} onClick={addPart} variant="outlined">Add Part</Button>
              </>
            )}

            {/* Follow-up */}
            {!editing && (ticket.followUpDate || ticket.followUpReason) && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Follow-up</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography variant="body2">{formatDate(ticket.followUpDate)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Time</Typography>
                    <Typography variant="body2">{ticket.followUpTime || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Reason</Typography>
                    <Typography variant="body2">{ticket.followUpReason || '—'}</Typography>
                  </Box>
                </Box>
              </>
            )}

            {/* Activity Log */}
            {!editing && ticket.activities && ticket.activities.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Activity Log</Typography>
                {ticket.activities.slice(0, 5).map((activity) => (
                  <Box key={activity.id} sx={{ display: 'flex', gap: 2, py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{(activity.action || '').replace(/_/g, ' ')}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{activity.description}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                      <Typography variant="caption" color="text.secondary" display="block">{formatDateTime(activity.createdAt)}</Typography>
                      {activity.user && (
                        <Typography variant="caption" color="text.secondary">{activity.user.firstName} {activity.user.lastName}</Typography>
                      )}
                    </Box>
                  </Box>
                ))}
                {ticket.activities.length > 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    +{ticket.activities.length - 5} more entries...
                  </Typography>
                )}
              </>
            )}

            {/* Inline panels */}
            {!editing && resolveOpen && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Mark as Resolved</Typography>
                <TextField label="Completion Notes (optional)" multiline rows={3} value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)} fullWidth size="small" sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => setResolveOpen(false)}>Cancel</Button>
                  <Button size="small" variant="contained" color="success" onClick={handleResolve}>Resolve</Button>
                </Box>
              </Box>
            )}

            {!editing && statusDialogOpen && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Change Status</Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel>New Status</InputLabel>
                  <Select value={newStatus} label="New Status" onChange={(e) => setNewStatus(e.target.value)}>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Resolved">Resolved</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                  <Button size="small" variant="contained" onClick={handleStatusChange}>Change</Button>
                </Box>
              </Box>
            )}

            {!editing && deleteConfirm && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid #d32f2f', borderRadius: 1, bgcolor: '#fff5f5' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>Are you sure you want to delete ticket {ticket.ticketNumber}?</Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => setDeleteConfirm(false)}>Cancel</Button>
                  <Button size="small" variant="contained" color="error" onClick={handleDelete}>Delete</Button>
                </Box>
              </Box>
            )}

            {!editing && reassignOpen && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid #1976d2', borderRadius: 1, bgcolor: '#f5f9ff' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Reassign Technician</Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel>Technician</InputLabel>
                  <Select value={reassignTechId} label="Technician" onChange={(e) => setReassignTechId(e.target.value)}>
                    {technicians.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.firstName} {t.lastName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => setReassignOpen(false)} disabled={reassigning}>Cancel</Button>
                  <Button size="small" variant="contained" onClick={handleReassign} disabled={reassigning || !reassignTechId}>
                    {reassigning ? <CircularProgress size={16} /> : 'Reassign'}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {ticket && !isReadOnly && !editing && !resolveOpen && !statusDialogOpen && !deleteConfirm && (
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
          <Box>
            {canDelete && ticket.status !== 'Resolved' && (
              <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteConfirm(true)}>Delete</Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canEdit && (
              <Button size="small" variant="outlined" color="secondary" startIcon={<DescriptionIcon />}
                onClick={() => setScaOpen(true)}>
                Sigler SCA Claim
              </Button>
            )}
            {canEdit && ticket.status !== 'Resolved' && (
              <>
                <Button size="small" variant="outlined" startIcon={<EditIcon />}
                  onClick={() => { setNewStatus(ticket.status === 'Active' ? 'In Progress' : 'Resolved'); setStatusDialogOpen(true); }}>
                  Change Status
                </Button>
                <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />}
                  onClick={() => setResolveOpen(true)}>
                  Resolve
                </Button>
              </>
            )}
            {isAdmin && ticket.status === 'Resolved' && (
              <Button size="small" variant="outlined" color="warning" startIcon={<RestoreIcon />}
                onClick={() => { setNewStatus('Active'); setStatusDialogOpen(true); }}>
                Reopen Ticket
              </Button>
            )}
          </Box>
        </DialogActions>
      )}

      <ScaFormDialog open={scaOpen} onClose={() => setScaOpen(false)} ticket={ticket} />
    </Dialog>
  );
}
